import { useState } from 'react';
import { deletionRequests } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Trash2, Clock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * DeleteConfirmDialog - Unified delete confirmation dialog
 * 
 * For admins: Deletes immediately with confirmation
 * For members: Creates a deletion request for admin approval
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Dialog open state change handler
 * @param {string} itemType - Type of item: task, client, contact, opportunity, quote, invoice, expense
 * @param {string} itemId - ID of the item to delete
 * @param {string} itemName - Display name of the item
 * @param {boolean} isAdmin - Whether current user is admin
 * @param {function} onDeleted - Callback when item is deleted/request created
 * @param {function} directDelete - Direct delete function for admins (optional)
 */
export default function DeleteConfirmDialog({ 
  open, 
  onOpenChange, 
  itemType, 
  itemId, 
  itemName,
  isAdmin,
  onDeleted,
  directDelete
}) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const itemTypeLabels = {
    task: 'tarea',
    client: 'cliente',
    contact: 'contacto',
    opportunity: 'oportunidad',
    quote: 'cotización',
    invoice: 'factura',
    expense: 'gasto',
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (isAdmin && directDelete) {
        // Admin can delete directly
        await directDelete(itemId);
        toast.success(`${itemTypeLabels[itemType]} eliminado exitosamente`);
        onDeleted?.();
      } else {
        // Member creates deletion request
        await deletionRequests.create(itemType, itemId, reason);
        toast.success('Solicitud de eliminación enviada. Un administrador debe aprobarla.');
        onDeleted?.();
      }
      onOpenChange(false);
      setReason('');
    } catch (err) {
      console.error('Error:', err);
      toast.error(err.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {isAdmin ? (
              <>
                <Trash2 className="h-5 w-5 text-destructive" />
                Eliminar {itemTypeLabels[itemType]}
              </>
            ) : (
              <>
                <Clock className="h-5 w-5 text-amber-500" />
                Solicitar eliminación
              </>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            {isAdmin ? (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">
                    Esta acción es permanente
                  </p>
                  <p className="text-sm">
                    ¿Estás seguro de eliminar "{itemName}"? Esta acción no se puede deshacer.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg">
                  <ShieldCheck className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-600">
                      Requiere aprobación de administrador
                    </p>
                    <p className="text-sm">
                      Tu solicitud para eliminar "{itemName}" será revisada por un administrador.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason">Razón de la eliminación (opcional)</Label>
                  <Textarea
                    id="reason"
                    placeholder="Explica por qué deseas eliminar este elemento..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={loading}
            className={isAdmin ? "bg-destructive hover:bg-destructive/90" : "bg-amber-500 hover:bg-amber-600"}
          >
            {loading ? 'Procesando...' : isAdmin ? 'Eliminar' : 'Enviar solicitud'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
